import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { GIT_VERSIONS_FILE, RELEASE_NOTES_APP } from '../../kiene-core.config';
import { UserRole } from '../stammdaten/benutzer/benutzer';
import { KieneSession } from '../../services/local-storage/kiene-session';
import { LocalStorageService } from '../../services/local-storage/local-storage.service';
import {
    compareVersions,
    ReleaseNoteHighlight,
    ReleaseNoteVersion,
    ReleaseNotesApp,
    ReleaseNotesDialogInput,
} from './release-notes';
import { ReleaseNotesDialogComponent } from './release-notes-dialog/release-notes-dialog.component';
import { ReleaseNotesMobileDialogComponent } from './release-notes-mobile-dialog/release-notes-mobile-dialog.component';

/** Automatischen Dialog nach Login/Client-Wechsel. Vor Release wieder aktivieren. */
const AUTO_SHOW_RELEASE_NOTES = false;

/** Zum Testen: Dialog bei jedem Login anzeigen. Vor Release wieder auf false setzen. */
const ALWAYS_SHOW_FOR_TESTING = false;

@Injectable({ providedIn: 'root' })
export class ReleaseNotesService {
    private dialogOpen = false;
    private scheduledCheck: ReturnType<typeof setTimeout>;
    private cachedNotes: ReleaseNoteVersion[];

    constructor(
        private readonly http: HttpClient,
        private readonly dialog: MatDialog,
        private readonly router: Router,
        private readonly localStorageService: LocalStorageService,
        @Inject(GIT_VERSIONS_FILE) private readonly versions: { version: string },
        @Optional() @Inject(RELEASE_NOTES_APP) private readonly app: ReleaseNotesApp,
    ) {}

    scheduleCheck(): void {
        if (!AUTO_SHOW_RELEASE_NOTES) {
            return;
        }
        clearTimeout(this.scheduledCheck);
        this.scheduledCheck = setTimeout(() => this.checkAndShow(), 800);
    }

    checkAndShow(): void {
        if (this.dialogOpen || this.isExcludedRoute()) {
            return;
        }

        const session = this.localStorageService.getCurrentUser();
        if (!session?.benutzer_id) {
            return;
        }

        const currentVersion = this.versions?.version;
        if (!currentVersion) {
            return;
        }

        const lastSeen = ALWAYS_SHOW_FOR_TESTING ? null : this.getLastSeenVersion(session.benutzer_id);
        if (!ALWAYS_SHOW_FOR_TESTING && lastSeen && compareVersions(currentVersion, lastSeen) <= 0) {
            return;
        }
        if (!ALWAYS_SHOW_FOR_TESTING && this.isDismissedForSession(session.benutzer_id, currentVersion)) {
            return;
        }

        this.loadReleaseNotes().subscribe(notes => {
            const relevantVersions = this.getRelevantVersions(notes, session, lastSeen, currentVersion);
            if (relevantVersions.length === 0) {
                this.markAsSeen(session.benutzer_id, currentVersion);
                return;
            }
            if (
                !ALWAYS_SHOW_FOR_TESTING &&
                lastSeen &&
                !notes.some(note => note.version === currentVersion) &&
                this.isPatchOnlyUpgrade(currentVersion, relevantVersions)
            ) {
                this.markAsSeen(session.benutzer_id, currentVersion);
                return;
            }

            this.openDialog(relevantVersions, currentVersion);
        });
    }

    /** Tour manuell öffnen (z. B. aus dem Profil-Menü). */
    showManually(): void {
        if (this.dialogOpen) {
            return;
        }

        const session = this.localStorageService.getCurrentUser();
        if (!session?.benutzer_id) {
            return;
        }

        const currentVersion = this.versions?.version;
        if (!currentVersion) {
            return;
        }

        this.loadReleaseNotes().subscribe(notes => {
            const relevantVersions = this.getRelevantVersions(notes, session, null, currentVersion, { manual: true });
            if (relevantVersions.length === 0) {
                return;
            }

            this.openDialog(relevantVersions, currentVersion);
        });
    }

    markAsSeen(benutzerId: number, version: string): void {
        localStorage.setItem(this.getStorageKey(benutzerId), version);
    }

    private openDialog(versions: ReleaseNoteVersion[], currentVersion: string): void {
        const input = new ReleaseNotesDialogInput();
        input.versions = versions;
        input.currentVersion = currentVersion;
        input.showAllReleaseNotesLink = this.app === 'desktop' && this.localStorageService.currentUserIsAdmin();

        const isKundenportal = this.app === 'kundenportal';
        this.dialogOpen = true;

        const dialogRef = isKundenportal
            ? this.dialog.open(ReleaseNotesMobileDialogComponent, {
                  width: '100vw',
                  maxWidth: '100vw',
                  height: '100dvh',
                  maxHeight: '100dvh',
                  panelClass: 'kiene-release-notes-mobile-dialog',
                  data: input,
                  disableClose: false,
              })
            : this.dialog.open(ReleaseNotesDialogComponent, {
                  width: '960px',
                  maxWidth: '96vw',
                  height: '840px',
                  maxHeight: '94vh',
                  panelClass: 'kiene-release-notes-dialog',
                  data: input,
                  disableClose: false,
              });

        this.bindDialogClose(dialogRef, currentVersion);
    }

    private bindDialogClose(
        dialogRef: { afterClosed: () => Observable<{ markAsSeen?: boolean } | undefined> },
        currentVersion: string,
    ): void {
        dialogRef.afterClosed().subscribe(result => {
            this.dialogOpen = false;
            const session = this.localStorageService.getCurrentUser();
            if (!session?.benutzer_id) {
                return;
            }
            if (result?.markAsSeen) {
                this.markAsSeen(session.benutzer_id, currentVersion);
                return;
            }
            this.dismissForSession(session.benutzer_id, currentVersion);
        });
    }

    private loadReleaseNotes(): Observable<ReleaseNoteVersion[]> {
        if (this.cachedNotes && !ALWAYS_SHOW_FOR_TESTING) {
            return of(this.cachedNotes);
        }

        return this.http.get<ReleaseNoteVersion[]>('./assets/versioninfo.json').pipe(
            tap(notes => {
                this.cachedNotes = notes;
            }),
            catchError(() => of([])),
        );
    }

    private getRelevantVersions(
        notes: ReleaseNoteVersion[],
        session: KieneSession,
        lastSeen: string | null,
        currentVersion: string,
        options?: { manual?: boolean },
    ): ReleaseNoteVersion[] {
        const manual = options?.manual ?? false;
        return notes
            .filter(note => {
                if (compareVersions(note.version, currentVersion) > 0) {
                    return false;
                }
                if (manual || ALWAYS_SHOW_FOR_TESTING) {
                    return note.version === currentVersion && this.versionHasVisibleContent(note, session);
                }
                if (lastSeen && compareVersions(note.version, lastSeen) <= 0) {
                    return false;
                }
                return this.versionHasVisibleContent(note, session);
            })
            .map(note => this.filterVersionForSession(note, session))
            .filter(note => this.versionHasVisibleContent(note, session));
    }

    private filterVersionForSession(note: ReleaseNoteVersion, session: KieneSession): ReleaseNoteVersion {
        const filtered = new ReleaseNoteVersion();
        filtered.version = note.version;
        filtered.date = note.date;
        filtered.highlights = (note.highlights ?? [])
            .filter(highlight => this.shouldIncludeHighlight(highlight, session))
            .sort((a, b) => this.compareHighlightOrder(a, b));
        filtered.items = note.items ?? [];
        return filtered;
    }

    private versionHasVisibleContent(note: ReleaseNoteVersion, session: KieneSession): boolean {
        const highlights = (note.highlights ?? []).filter(highlight => this.shouldIncludeHighlight(highlight, session));
        return highlights.length > 0;
    }

    private compareHighlightOrder(a: ReleaseNoteHighlight, b: ReleaseNoteHighlight): number {
        return this.getHighlightSortRank(a) - this.getHighlightSortRank(b);
    }

    private getHighlightSortRank(highlight: ReleaseNoteHighlight): number {
        if (highlight.title === 'Release Notes nach Updates') {
            return 90;
        }
        if (highlight.title?.startsWith('Weiter mit:')) {
            return 50;
        }
        const isKundenportalOnly =
            highlight.apps?.includes('kundenportal') && !highlight.apps?.includes('desktop');
        if (this.app === 'kundenportal' && isKundenportalOnly) {
            return 10;
        }
        if (this.app === 'kundenportal' && highlight.apps?.includes('kundenportal')) {
            return 20;
        }
        if (isKundenportalOnly) {
            return 60;
        }
        return 10;
    }

    private shouldIncludeHighlight(highlight: ReleaseNoteHighlight, session: KieneSession): boolean {
        if (this.isGruppenHighlightHiddenForExcludedRoles(highlight, session)) {
            return false;
        }
        if (!this.matchesApp(highlight.apps)) {
            return false;
        }
        const currentApp = this.app ?? 'desktop';
        if (currentApp === 'kundenportal') {
            if (highlight.title?.startsWith('Weiter mit:')) {
                return false;
            }
            if (highlight.title === 'Release Notes nach Updates') {
                return false;
            }
            if (highlight.apps?.includes('desktop') && !highlight.apps?.includes('kundenportal')) {
                return false;
            }
        }
        return this.matchesRoles(session, highlight.roles);
    }

    private matchesApp(apps?: ReleaseNotesApp[]): boolean {
        if (!apps || apps.length === 0) {
            return true;
        }
        const currentApp = this.app ?? 'desktop';
        if (currentApp === 'desktop') {
            return apps.includes('desktop') || apps.includes('kundenportal');
        }
        return apps.includes('kundenportal');
    }

    private matchesRoles(session: KieneSession, roles?: string[]): boolean {
        if (!roles || roles.length === 0) {
            return true;
        }
        if ((session.benutzerrollen ?? []).some(role => roles.some(r => this.roleMatches(role, r)))) {
            return true;
        }
        return Number(session.admin) === 1;
    }

    /** Gruppen-Dashboard/Sammelaktionen: nicht für Apotheke- oder reine Labor-Rollen. */
    private isGruppenHighlightHiddenForExcludedRoles(
        highlight: ReleaseNoteHighlight,
        session: KieneSession,
    ): boolean {
        const title = highlight.title ?? '';
        if (title !== 'Gruppen Aktionen' && title !== 'Sammelaktionen in Gruppen-Details') {
            return false;
        }
        const roles = session.benutzerrollen ?? [];
        if (roles.some(r => r.apotheke > 0)) {
            return true;
        }
        const hasLabor = roles.some(r => r.labor > 0);
        if (!hasLabor) {
            return false;
        }
        const hasGruppenRelevantRole = roles.some(
            r => r.kunde > 0 || r.tierarzt > 0 || r.viehhaendler > 0 || r.verwaltung > 0,
        );
        return !hasGruppenRelevantRole;
    }

    private roleMatches(userRole: UserRole, role: string): boolean {
        const key = role.toLowerCase();
        switch (key) {
            case 'tierarzt':
                return userRole.tierarzt > 0;
            case 'kunde':
            case 'landwirt':
                return userRole.kunde > 0;
            case 'viehhaendler':
                return userRole.viehhaendler > 0;
            case 'verwaltung':
                return userRole.verwaltung > 0;
            case 'admin':
                return userRole.admin_der_rolle > 0;
            case 'labor':
                return userRole.labor > 0;
            case 'apotheke':
                return userRole.apotheke > 0;
            case 'sgs':
                return userRole.sgs > 0;
            case 'schlachthof':
                return userRole.schlachthof > 0;
            case 'spediteur':
                return userRole.spediteur > 0;
            case 'support':
                return userRole.support > 0;
            case 'praxis':
                return userRole.praxis > 0;
            default:
                return userRole.bezeichnung?.toLowerCase().includes(key);
        }
    }

    private getLastSeenVersion(benutzerId: number): string | null {
        return localStorage.getItem(this.getStorageKey(benutzerId));
    }

    private getStorageKey(benutzerId: number): string {
        return `kieneReleaseNotesSeen_${benutzerId}`;
    }

    private getSessionDismissKey(benutzerId: number): string {
        return `kieneReleaseNotesDismissed_${benutzerId}`;
    }

    private dismissForSession(benutzerId: number, version: string): void {
        sessionStorage.setItem(this.getSessionDismissKey(benutzerId), version);
    }

    private isDismissedForSession(benutzerId: number, version: string): boolean {
        return sessionStorage.getItem(this.getSessionDismissKey(benutzerId)) === version;
    }

    /** Patch-Release ohne eigenen versioninfo-Eintrag (z. B. 25.45.5 mit nur 25.45.0-Notes). */
    private isPatchOnlyUpgrade(currentVersion: string, relevantVersions: ReleaseNoteVersion[]): boolean {
        const newestNoteVersion = relevantVersions.reduce(
            (max, note) => (compareVersions(note.version, max) > 0 ? note.version : max),
            relevantVersions[0].version,
        );
        return (
            compareVersions(currentVersion, newestNoteVersion) > 0 &&
            this.getReleaseLine(currentVersion) === this.getReleaseLine(newestNoteVersion)
        );
    }

    private getReleaseLine(version: string): string {
        const parts = version.split('.');
        return `${parts[0] ?? 0}.${parts[1] ?? 0}`;
    }

    private isExcludedRoute(): boolean {
        const url = this.router.url?.split('?')[0] ?? '';
        return (
            url.startsWith('/login') ||
            url.startsWith('/register') ||
            url.startsWith('/sign') ||
            url.startsWith('/initial-setup')
        );
    }
}
